import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getMyClubs, type ClubBranding } from "@/application/api/club";
import {
  getStoredClubId,
  setStoredClubId,
} from "@/application/utils/activeClubStorage";
import { deriveClubTheme } from "@/application/utils/deriveClubTheme";
import vuetify from "@/application/plugins/vuetify";

const defaultColors = { ...vuetify.theme.themes.value.light.colors };

const applyClubTheme = (club: ClubBranding) => {
  const derived = club.primaryColor ? deriveClubTheme(club.primaryColor) : null;

  const theme = vuetify.theme.themes.value.light;
  theme.colors = { ...defaultColors, ...derived };
};

export const useClubStore = defineStore("club", () => {
  const clubs = ref<ClubBranding[]>([]);
  const activeClubId = ref<string | null>(null);
  const isLoaded = ref(false);

  const club = computed(
    () => clubs.value.find((c) => c.id === activeClubId.value) ?? null,
  );

  let loadPromise: Promise<ClubBranding | null> | null = null;

  const fetch = async () => {
    try {
      clubs.value = await getMyClubs();

      const storedClubId = getStoredClubId();
      const preferredClub = clubs.value.find((c) => c.id === storedClubId);
      activeClubId.value = (preferredClub ?? clubs.value[0])?.id ?? null;

      if (club.value) applyClubTheme(club.value);
      return club.value;
    } finally {
      isLoaded.value = true;
    }
  };

  const ensureLoaded = () => {
    if (!loadPromise) {
      loadPromise = fetch().catch((error) => {
        loadPromise = null;
        throw error;
      });
    }
    return loadPromise;
  };

  const switchClub = (clubId: string) => {
    if (clubId === activeClubId.value) return;
    if (!clubs.value.some((c) => c.id === clubId)) return;

    setStoredClubId(clubId);
    window.location.reload();
  };

  return {
    clubs,
    club,
    activeClubId,
    isLoaded,
    fetch,
    ensureLoaded,
    switchClub,
  };
});
